import React, {useCallback, useContext, useEffect, useState} from 'react';
import {Box} from '@mui/material';
import {getMarkerTop, markerComponentByType} from './markerUtils';
import {getMarkerTheme} from './markerTheme';
import useClaimDetailsDialogStore from '../../../../stores/useClaimDetailsDialogStore';
import RailModeContext from '../RailModeContext';

/**
 * Small icon badge rendered in the icon-strip (collapsed rail) for a marker.
 * Uses markerTheme directly — no marker component is mounted, zero Yjs subscriptions.
 * Active state is shown via a thicker border + glow ring.
 */
const MarkerIconBadge = React.memo(function MarkerIconBadge({marker, isActive, onClick}) {
    const cfg = getMarkerTheme(marker.type);
    const Icon = cfg.Icon;
    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick?.(e);
                }
            }}
            sx={{
                width: 32,
                height: 32,
                borderRadius: 999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: cfg.bg,
                border: isActive ? `2px solid ${cfg.border}` : `1px solid ${cfg.border}`,
                boxShadow: isActive ? `0 0 0 3px ${cfg.bg}` : 'none',
                cursor: 'pointer',
                transition: 'box-shadow 0.15s ease, border-width 0.15s ease',
                flex: '0 0 auto',
            }}
        >
            {Icon ? <Icon fontSize="small" sx={{color: cfg.color}}/> : null}
        </Box>
    );
});

const MarkerItem = React.memo(({
                                   marker,
                                   active,
                                   onMarkerClickRef,
                                   onMarkerDeleteRef,
                                   onThreadSnapshotRef,
                                   reportId,
                                   view
                               }) => {
    const isClaimDialogOpen = useClaimDetailsDialogStore(s => s.isOpen);
    const railMode = useContext(RailModeContext);
    const iconOnly = railMode === 'icon';

    const isActive = !!(active && active.type === marker.type && active.uid === marker.uid);
    const isCreated = isActive && active?.created === true;

    // In icon mode, the full card is only shown when the user explicitly clicks the
    // badge — NOT when the active state is driven from an editor text selection.
    const [iconExpanded, setIconExpanded] = useState(false);

    // Collapse the card overlay whenever the marker loses its active state
    useEffect(() => {
        if (!isActive) setIconExpanded(false);
    }, [isActive]);

    const handleClick = useCallback((e) => {
        if (isClaimDialogOpen) return;
        e.preventDefault();
        e.stopPropagation();
        onMarkerClickRef?.current?.(marker);
    }, [isClaimDialogOpen, marker, onMarkerClickRef]);

    // Badge-specific click: notify the parent AND open the card overlay
    const handleBadgeClick = useCallback((e) => {
        if (isClaimDialogOpen) return;
        e.preventDefault();
        e.stopPropagation();
        onMarkerClickRef?.current?.(marker);
        setIconExpanded(true);
    }, [isClaimDialogOpen, marker, onMarkerClickRef]);

    const handleDelete = useCallback((markerToDelete) => {
        onMarkerDeleteRef?.current?.(markerToDelete);
    }, [onMarkerDeleteRef]);

    const top = getMarkerTop(marker);
    if (top == null) return null;

    const Comp = markerComponentByType[marker.type];
    if (!Comp) return null;

    if (iconOnly) {
        const showCard = isActive && iconExpanded;
        return (
            <Box
                sx={{
                    position: 'absolute',
                    top,
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    transition: 'top 200ms ease-out',
                }}
            >
                {/* Icon badge — always visible, no component mounted */}
                <MarkerIconBadge
                    marker={marker}
                    isActive={isActive}
                    onClick={handleBadgeClick}
                />

                {/* Full card overlay — only when the user explicitly clicked the badge */}
                {showCard ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            minWidth: 'var(--pm-margin-rail-width)',
                            zIndex: 999,
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Comp
                            marker={marker}
                            isActive={true}
                            created={isCreated}
                            onClick={handleClick}
                            onDelete={handleDelete}
                            onThreadSnapshot={onThreadSnapshotRef?.current}
                            reportId={reportId || marker.reportId || null}
                            view={view}
                        />
                    </Box>
                ) : null}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                position: 'absolute',
                top,
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
                transition: 'top 200ms ease-out',
            }}
            onClick={handleClick}
        >
            <Comp
                marker={marker}
                isActive={isActive}
                created={isCreated}
                onClick={handleClick}
                onDelete={handleDelete}
                onThreadSnapshot={onThreadSnapshotRef?.current}
                reportId={reportId || marker.reportId || null}
                view={view}
            />
        </Box>
    );
}, (prev, next) => {
    const prevIsActive = !!(prev.active && prev.active.type === prev.marker.type && prev.active.uid === prev.marker.uid);
    const nextIsActive = !!(next.active && next.active.type === next.marker.type && next.active.uid === next.marker.uid);
    const prevCreated = prevIsActive && prev.active?.created === true;
    const nextCreated = nextIsActive && next.active?.created === true;
    return prev.marker === next.marker
        && prev.active?.type === next.active?.type
        && prev.active?.uid === next.active?.uid
        && prevCreated === nextCreated
        && prev.onMarkerClickRef === next.onMarkerClickRef
        && prev.onMarkerDeleteRef === next.onMarkerDeleteRef
        && prev.onThreadSnapshotRef === next.onThreadSnapshotRef
        && prev.view === next.view;
});

export default MarkerItem;
